import {
  ButtonActor,
  Card,
  Checkbox,
  FieldGroup,
  InformationIcon,
  LinkActor,
  LockIcon,
  Modal,
  Row,
  Subtitle,
  Title,
  UserIdentifiedCheckBigIcon,
} from 'internalLibrary';
import { ChangeEvent, FC, useCallback, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { R } from 'config/app';
import { TinkIFrame } from '../TinkIFrame/TinkIFrame';
import { TinkMessageEvent } from '../TinkIFrame/parseTinkMessageEvent';
import { useAnalytics } from 'use-analytics';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import layout from '../../../../../scss/layout.module.scss';
import postTinkConsentApiCall from 'api/calls/postTinkConsentApiCall';
import style from './Tink.module.scss';
import useTinkInquiriesMutation from 'ui/hooks/useTinkInquiriesMutation';

interface TinkProps {
  personalId?: string;
  loanApplicationId?: number;
}

const IS_PROD = process.env.REACT_APP_ENV === 'production';

const Tink: FC<TinkProps> = (props) => {
  const { personalId, loanApplicationId } = props;
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const [tinkConsent, setTinkConsent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [modalOpened, setModalOpened] = useState(false);
  const navigate = useNavigate();

  const tinkConsentMutation = useMutation({
    mutationKey: ['client', 'consent', 'tink'],
    mutationFn: postTinkConsentApiCall,
  });
  const tinkInquiriesMutation = useTinkInquiriesMutation();

  const tinkConsentChanged = (e: ChangeEvent<HTMLInputElement>) => {
    setTinkConsent((prev) => {
      const newValue = !prev;

      if (newValue) setErrorMessage('');
      else setErrorMessage(t('requiredField'));

      track('checkbox', {
        desciption: 'tink consent',
        checked: e.target.checked,
      });
      return newValue;
    });
  };

  const identifyOnlineClicked = useCallback(
    (presetDone?: boolean) => {
      if (!tinkConsent) {
        setErrorMessage(t('requiredField'));
        return;
      } else {
        setErrorMessage('');
      }

      if (!document.body) return;

      // if (!presetDone) {
      //   if (process.env.REACT_APP_ENV !== 'production') {
      //     setModalPresetOpened(true);
      //     return;
      //   }
      // } else {
      //   setModalPresetOpened(false);
      // }

      document.body.style.height = '100%';
      document.body.style.overflow = 'hidden';
      setModalOpened(true);
    },
    [tinkConsent],
  );

  const closeTinkTriggered = useCallback(() => {
    if (!document.body) {
      return;
    }
    document.body.style.height = '100%';
    document.body.style.overflow = 'visible';
    setModalOpened(false);
  }, []);

  const tinkEventOccurred = async (payload: TinkMessageEvent) => {
    const { data, type, error } = payload;

    if (type !== 'status') {
      track('tink', payload);
      datadogLogs.logger.info('Tink event', { tink: { data, type, error } });
    }

    if (type === 'error') {
      // TODO: show error message in UI
      switch (error?.status) {
        case 'USER_CANCELLED':
          closeTinkTriggered();
          return;
        case 'BAD_REQUEST':
        case 'INTERNAL_ERROR':
        case 'TEMPORARY_ERROR':
          // TODO: Handle error
          closeTinkTriggered();
          return;
        case 'AUTHENTICATION_ERROR':
          // TODO: Handle error
          closeTinkTriggered();
      }
    } else if (type === 'reports') {
      await tinkConsentMutation.mutateAsync();
      await tinkInquiriesMutation.postTinkInquiries({
        productName: 'vivus.se',
        personalId, //: client.personalId,
        loanApplicationId, //: application.id,
        accountVerificationReportId: IS_PROD
          ? data.account_verification_report_id
          : personalId,
        transactionReportId: IS_PROD ? data.transaction_report_id : personalId,
      });
      navigate(R.FirstLoanTinkLoading);
    }
  };

  const manualIdentificationClicked = () => {
    navigate(R.FirstLoanIdentificationMethod);
  };

  return (
    <>
      <Modal className={style.modal} isOpen={modalOpened} isMobileFullWidth>
        <TinkIFrame onTinkEvent={tinkEventOccurred} />
      </Modal>
      <div className={layout['width-lg']}>
        <Title text={t('tink_title')} className={style.title} />

        <Card intent="warning" className={style['warning-card']}>
          <Row className={style.row}>
            <InformationIcon className={style['info-icon']} />
            <Subtitle
              heading={t('tink_card_warningTitle')}
              text={t('tink_card_warningText')}
              className={style['warning-card-text']}
            />
          </Row>
        </Card>
        <Card className={style.card}>
          <Row className={style['card-row']}>
            <div className={style['card-left']}>
              <FieldGroup validationText={errorMessage}>
                <div>
                  <Checkbox
                    value={tinkConsent}
                    onChange={tinkConsentChanged}
                    text={t('tink_card_checkbox')}
                    errors={Boolean(errorMessage)}
                  />
                </div>
              </FieldGroup>
              <ButtonActor
                className={style.button}
                buttonProps={{
                  onClick: () => identifyOnlineClicked(),
                }}
                analytics={{
                  description: 'submit',
                }}
              >
                {t('tink_card_button')}
              </ButtonActor>
              <Row className={style['secure-row']}>
                <LockIcon className={style['lock-icon']} />
                <span className={style['secure-text']}>
                  {t('tink_card_secureText')}
                </span>
              </Row>
            </div>
            <div className={style['card-right']}>
              <UserIdentifiedCheckBigIcon className={style['user-icon']} />
            </div>
          </Row>
        </Card>
        <Row className={style['manual']}>
          <InformationIcon className={style['info-icon']} />
          <span>
            {t('tink_manual')}{' '}
            <button type="button" onClick={manualIdentificationClicked}>
              <LinkActor
                className={style['manual-link']}
                analytics={{
                  description: 'link to manual',
                }}
              >
                {t('tink_manualLink')}
              </LinkActor>
            </button>
          </span>
        </Row>
      </div>
    </>
  );
};

export default Tink;
